import { api } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { HOME_BROWSE_PATH, SEARCH_PATH } from 'src/common/constants/paths';
import type { RouteProps } from 'src/common/routes/utils/deriveRouteProps';
import { getSelectCategory } from 'src/common/selectors/categories';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { getSelectProfile } from 'src/common/selectors/profiles';
import { getClientAppServices } from 'src/common/services';
import { isCategory, isValidGuideId } from 'src/common/utils/guideItemTypes';

const AD_ELIGIBLE_PATHNAMES_MAP: Record<string, string> = {
  [HOME_BROWSE_PATH]: 'Home',
  [SEARCH_PATH]: 'Search',
};
const AD_INELIGIBLE_PATHNAMES_MAP: Record<string, string> = {
  '/': 'Landing',
  '/login/': 'Login',
  '/subscribe/': 'Subscribe',
  '/paymentcomplete/': 'Subscribe|PaymentComplete',
  '/explorer/': 'Explorer',
  '/account/': 'Settings|Account',
  '/devices/': 'Settings|Devices',
  '/payment/': 'Settings|Subscription',
};

class ScreenViewTracker {
  #pathname?: string;
  #guideId?: string;
  #isCategory = false;
  #isAdEligible = false;
  #hasNonAdEligibleOverlay = false;

  init() {
    const { playerStore, appStore } = getClientAppServices().stores;
    // In the future, the store will be migrated from Redux to Zustand, which provides state/prevState args,
    // so let's not add these as class properties.
    let _previousIsNowPlayingDialogOpen = false;
    let _previousTunedGuideId = '';

    const onStoresUpdate = () => {
      const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(storeSingleton.state);
      const tunedGuideId = playerStore.getState().currentGuideItem?.playableGuideId || '';

      try {
        if (
          (isNowPlayingDialogOpen && !_previousIsNowPlayingDialogOpen) ||
          (isNowPlayingDialogOpen && tunedGuideId !== _previousTunedGuideId)
        ) {
          api.telemetry.setScreen({
            name: 'NowPlaying',
            guideId: tunedGuideId,
            isDisplayAdEligible: !api.isPremium,
          });
          return;
        }

        if (!isNowPlayingDialogOpen && _previousIsNowPlayingDialogOpen) {
          this.updateScreen();
          return;
        }
      } finally {
        _previousIsNowPlayingDialogOpen = isNowPlayingDialogOpen;
        _previousTunedGuideId = tunedGuideId;
      }
    };

    storeSingleton.subscribe(onStoresUpdate);
    playerStore.subscribe(onStoresUpdate);

    // TODO: add premium event to WAC
    let prevIsPremium = api.isPremium;

    api.on('authTokensUpdated', () => {
      if (prevIsPremium === api.isPremium) {
        return;
      }

      prevIsPremium = api.isPremium;

      const { routeProps } = appStore.getState();

      // Before the app's first render; its own route change will report the screen.
      if (!routeProps) {
        return;
      }

      this.#pathname = undefined;
      this.onRouteChange(routeProps);
    });
  }

  onRouteChange(routeProps: RouteProps, hasNonAdEligibleOverlay = false) {
    if (
      routeProps.matchUrl === this.#pathname &&
      hasNonAdEligibleOverlay === this.#hasNonAdEligibleOverlay
    ) {
      return;
    }

    this.#guideId = routeProps.guideContext.guideId;
    this.#isCategory = !isValidGuideId(this.#guideId as string) || isCategory(this.#guideId);

    const guideItem = this.#isCategory
      ? getSelectCategory(this.#guideId)(storeSingleton.state)
      : getSelectProfile(this.#guideId)(storeSingleton.state);

    this.#isAdEligible = Boolean(guideItem?.metadata?.properties?.ads?.adEligible ?? true);
    this.#hasNonAdEligibleOverlay = hasNonAdEligibleOverlay;

    this.updateScreen(routeProps.matchUrl as string);
  }

  updateScreen(pathname?: string) {
    this.#pathname = pathname || this.#pathname;
    const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(storeSingleton.state);

    if (!this.#pathname || isNowPlayingDialogOpen) {
      return;
    }

    // This is the case when we display an overlay that is not ad eligible (e.g., MobileWebEntry).
    if (this.#hasNonAdEligibleOverlay) {
      api.telemetry.setScreen({
        name: 'ScreenBlockerOverlay',
        guideId: this.#guideId,
        isDisplayAdEligible: false,
      });
      return;
    }

    if (AD_ELIGIBLE_PATHNAMES_MAP[this.#pathname]) {
      api.telemetry.setScreen({
        name: AD_ELIGIBLE_PATHNAMES_MAP[this.#pathname],
        guideId: this.#guideId,
        isDisplayAdEligible: !api.isPremium,
      });
      return;
    }

    if (AD_INELIGIBLE_PATHNAMES_MAP[this.#pathname]) {
      api.telemetry.setScreen({
        name: AD_INELIGIBLE_PATHNAMES_MAP[this.#pathname],
        guideId: this.#guideId,
        isDisplayAdEligible: false,
      });
      return;
    }

    if (this.#pathname.startsWith('/user/')) {
      api.telemetry.setScreen({
        name: 'Library',
        isDisplayAdEligible: !api.isPremium,
      });
      return;
    }

    // The existence of a guide id implies a category/profile route.
    if (this.#guideId) {
      api.telemetry.setScreen({
        name: this.#isCategory ? 'Browse' : 'Profile',
        guideId: this.#guideId,
        isDisplayAdEligible: this.#isAdEligible,
      });
      return;
    }

    api.telemetry.setScreen({
      name: this.#pathname,
      isDisplayAdEligible: false,
    });
  }
}

export const screenViewTracker = new ScreenViewTracker();
